import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import type { Slot, TimetableGrid } from '../../../services/timetableService';
import { DAY_SHORT, gridColumns, orderedDays, periodsOn } from '../timetableUtils';

export type Brush = 'unavailable' | 'preferNot' | 'available';

interface AvailabilityGridProps {
  grid: TimetableGrid;
  unavailable: Slot[];
  preferNot: Slot[];
  brush: Brush;
  readOnly: boolean;
  onChange: (next: { unavailable: Slot[]; preferNot: Slot[] }) => void;
}

const key = (d: number, p: number) => `${d}:${p}`;
const toSlots = (set: Set<string>): Slot[] => Array.from(set).map((k) => {
  const [day, period] = k.split(':').map(Number);
  return { day, period };
}).sort((a, b) => a.day - b.day || a.period - b.period);

/**
 * Days × periods to paint: click or drag across cells with the chosen brush.
 * Clicking a day or a period header paints the whole row or column.
 */
const AvailabilityGrid = memo<AvailabilityGridProps>(({ grid, unavailable, preferNot, brush, readOnly, onChange }) => {
  const theme = useTheme();
  const columns = useMemo(() => gridColumns(grid), [grid]);
  const days = useMemo(() => orderedDays(grid), [grid]);
  const un = useMemo(() => new Set(unavailable.map((s) => key(s.day, s.period))), [unavailable]);
  const pn = useMemo(() => new Set(preferNot.map((s) => key(s.day, s.period))), [preferNot]);
  const painting = useRef(false);
  // Painting edits a working copy, so one drag is one change.
  const work = useRef<{ un: Set<string>; pn: Set<string> } | null>(null);

  const paint = useCallback((cells: [number, number][]) => {
    if (readOnly) return;
    const w = work.current ?? { un: new Set(un), pn: new Set(pn) };
    work.current = w;
    for (const [d, p] of cells) {
      if (p > periodsOn(grid, d)) continue;
      const k = key(d, p);
      w.un.delete(k); w.pn.delete(k);
      if (brush === 'unavailable') w.un.add(k);
      if (brush === 'preferNot') w.pn.add(k);
    }
    onChange({ unavailable: toSlots(w.un), preferNot: toSlots(w.pn) });
  }, [readOnly, un, pn, brush, grid, onChange]);

  /** A header click is a one-off stroke. */
  const paintOnce = (cells: [number, number][]) => { work.current = null; paint(cells); work.current = null; };

  useEffect(() => {
    const stop = () => { painting.current = false; work.current = null; };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const periodCols = columns.filter((c) => c.kind === 'period') as Extract<typeof columns[number], { kind: 'period' }>[];
  const template = `64px ${columns.map((c) => (c.kind === 'period' ? 'minmax(44px, 1fr)' : '10px')).join(' ')}`;
  const tone = {
    unavailable: theme.palette.error.main,
    preferNot: theme.palette.warning.main,
  };

  const headerSx = {
    py: 0.75, textAlign: 'center' as const, cursor: readOnly ? 'default' : 'pointer', borderRadius: '6px',
    '&:hover': readOnly ? undefined : { bgcolor: 'action.hover' },
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: template, gap: '4px', minWidth: 64 + periodCols.length * 48, userSelect: 'none' }}>
        <Box />
        {columns.map((c, i) => (c.kind === 'break'
          ? <Box key={`b${i}`} />
          : (
            <Box key={`p${c.period}`} sx={headerSx} onClick={() => paintOnce(days.map((d) => [d, c.period]))}>
              <Typography variant="caption" fontWeight={700} display="block">{c.period}</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{c.start}</Typography>
            </Box>
          )))}

        {days.map((d) => (
          <React.Fragment key={d}>
            <Box sx={{ ...headerSx, textAlign: 'left', px: 1, display: 'flex', alignItems: 'center' }} onClick={() => paintOnce(periodCols.map((c) => [d, c.period]))}>
              <Typography variant="body2" fontWeight={600}>{DAY_SHORT[d]}</Typography>
            </Box>
            {columns.map((c, i) => {
              if (c.kind === 'break') {
                return <Box key={`b${i}`} sx={{ borderRadius: '3px', bgcolor: alpha(theme.palette.text.secondary, 0.08) }} />;
              }
              const off = c.period > periodsOn(grid, d);
              const k = key(d, c.period);
              const state: Brush = un.has(k) ? 'unavailable' : pn.has(k) ? 'preferNot' : 'available';
              const color = state === 'available' ? null : tone[state];
              return (
                <Box
                  key={k}
                  role="button"
                  aria-label={`${DAY_SHORT[d]} period ${c.period}: ${state}`}
                  onMouseDown={(e) => { if (off) return; e.preventDefault(); painting.current = true; work.current = null; paint([[d, c.period]]); }}
                  onMouseEnter={() => { if (painting.current && !off) paint([[d, c.period]]); }}
                  sx={{
                    height: 36,
                    borderRadius: '8px',
                    cursor: off || readOnly ? 'default' : 'crosshair',
                    border: '1px solid',
                    transition: 'background-color 80ms',
                    ...(off
                      ? { borderColor: 'transparent', background: `repeating-linear-gradient(45deg, transparent, transparent 4px, ${alpha(theme.palette.text.secondary, 0.08)} 4px, ${alpha(theme.palette.text.secondary, 0.08)} 8px)` }
                      : color
                        ? { borderColor: alpha(color, 0.5), bgcolor: alpha(color, state === 'unavailable' ? 0.22 : 0.18) }
                        : { borderColor: 'divider', bgcolor: alpha(theme.palette.success.main, 0.05) }),
                  }}
                />
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
});

AvailabilityGrid.displayName = 'AvailabilityGrid';

export default AvailabilityGrid;
